import { Injectable } from '@angular/core';
import { LimitedCurrencyAmount } from '@lx/currency/shared/model/currency.model';
import { Amount } from '@lx/shared/data-access/types';
import { ComponentStore } from '@ngrx/component-store';
import { ProductPrices, QuoteBreakdown } from '../../types';
import { QuoteOutputProduct, QuoteState } from './quote.state';

@Injectable({ providedIn: 'root' })
export class QuoteStore extends ComponentStore<QuoteState> {
  //Selectors
  readonly mostUrgentDiscountExpiryDate$ = this.select(({ discounts }) => {
    if (!discounts) {
      return;
    }

    const mostUrgentDiscount = discounts.reduce((mostUrgent, discount) => {
      if (!mostUrgent || discount.expirationDate < mostUrgent.expirationDate) {
        return discount;
      }
      return mostUrgent;
    }, undefined);

    return mostUrgentDiscount?.expirationDate;
  });

  readonly quoteCourse$ = this.select(({ course }) => course);
  readonly accommodation$ = this.select(({ accommodation }) => accommodation);
  readonly transfers$ = this.select(({ transfers }) => transfers);
  readonly transferTotalPrices$ = this.select(({ transfers }) => {
    return this.calculateProductsTotalPricesPrices(transfers || []);
  });
  readonly transfersWithTotalPrices$ = this.select(
    this.transfers$,
    this.transferTotalPrices$,
    (transfers: QuoteOutputProduct[], totalPrices: ProductPrices) => ({
      transfers: transfers || [],
      totalPrices,
    }),
  );
  readonly feesAndSupplements$ = this.select(({ feesAndSupplements }) => feesAndSupplements);
  readonly insurance$ = this.select(({ insurance }) => insurance);
  readonly premiumResidence$ = this.select(({ premiumResidence }) => premiumResidence);
  readonly professionalImmersionExperience$ = this.select(({ professionalImmersionExperience }) => professionalImmersionExperience);
  readonly privateLessons$ = this.select(({ privateLessons }) => privateLessons);
  readonly unaccompaniedMinorServices$ = this.select(({ unaccompaniedMinorServices }) => unaccompaniedMinorServices);
  readonly premiumActivities$ = this.select(({ premiumActivities }) => premiumActivities);
  readonly grandTotal$ = this.select(({ grandTotal }) => grandTotal);
  readonly singleCurrencyMode$ = this.select(({ singleCurrencyMode }) => singleCurrencyMode);
  readonly grandTotalAndSingleCurrencyMode$ = this.select(
    ({ grandTotal, singleCurrencyMode }) => ({
      grandTotal,
      singleCurrencyMode,
    }),
    {
      debounce: true,
    },
  );

  readonly packageDetails$ = this.select(
    ({ grandTotal, productTotal, payableData }) => ({
      grandTotal,
      productTotal,
      payableData,
    }),
    { debounce: true },
  );

  readonly payableData$ = this.select(({ payableData }) => payableData);

  readonly quoteBreakdown$ = this.select(
    ({
      course,
      accommodation,
      transfers,
      insurance,
      premiumResidence,
      premiumActivities,
      privateLessons,
      professionalImmersionExperience,
      feesAndSupplements,
      unaccompaniedMinorServices,
    }) => {
      return {
        course,
        accommodation: this.prepareAccommodationForBreakdown(accommodation),
        transfer: this.prepareTransfersForBreakdown(transfers),
        insurance,
        premiumResidence,
        premiumActivities: this.preparePremiumActivitiesForBreakdown(premiumActivities),
        privateLessons,
        professionalImmersionExperience,
        feesAndSupplements,
        unaccompaniedMinorServices: this.prepareUnaccompaniedMinorServicesForBreakdown(unaccompaniedMinorServices),
      } as QuoteBreakdown;
    },
    { debounce: true },
  );

  readonly selectionForDataLayer$ = this.select(
    ({
      accommodation,
      insurance,
      transfers,
      professionalImmersionExperience,
      productTotal,
      grandTotal,
      destinationCurrency,
      displayCurrency,
      discountTotalAsPercentage,
      discountTotal,
    }) => ({
      accommodationName: accommodation?.name,
      insurance,
      transfers,
      professionalImmersionExperience,
      productTotal,
      grandTotal,
      destinationCurrency,
      displayCurrency,
      discountTotalAsPercentage,
      discountTotal,
    }),
    {
      debounce: true,
      equal: (previous, current) => previous.productTotal === current.productTotal && previous.grandTotal === current.grandTotal,
    },
  );

  readonly pricesSelectionForDataLayer$ = this.select(
    ({ productTotal, grandTotal, destinationCurrency, displayCurrency, discountTotalAsPercentage, discountTotal }) => ({
      productTotal,
      grandTotal,
      destinationCurrency,
      displayCurrency,
      discountTotalAsPercentage,
      discountTotal,
    }),
    {
      debounce: true,
      equal: (previous, current) => previous.productTotal === current.productTotal && previous.grandTotal === current.grandTotal,
    },
  );

  // Updaters
  readonly updateState = this.updater((state, partialState: Partial<QuoteState>) => ({
    ...state,
    ...partialState,
  }));

  constructor() {
    super({});
  }

  resetState() {
    this.setState({});
  }

  /**
   * Prepares accommodation for breakdown display. The name of the accommodation has to be split into name and description.
   * The description is everything after the first colon in the name.
   */
  private prepareAccommodationForBreakdown(accommodation: QuoteOutputProduct) {
    if (!accommodation || Object.keys(accommodation).length === 0) {
      return undefined;
    }
    let description = '';
    const namePieces = accommodation.name.split(':');
    if (namePieces.length > 1) {
      description = namePieces[1].trim();
    }

    return {
      ...accommodation,
      name: namePieces[0]?.trim(),
      description,
    };
  }

  private prepareTransfersForBreakdown(transfers: QuoteOutputProduct[]) {
    if (!transfers?.length) {
      return undefined;
    }
    return {
      transfers: transfers?.map((transfer) => ({ ...transfer })),
      prices: this.calculateProductsTotalPricesPrices(transfers || []),
    };
  }

  private preparePremiumActivitiesForBreakdown(premiumActivities: QuoteOutputProduct[]) {
    if (!premiumActivities?.length) {
      return undefined;
    }
    return {
      activities: premiumActivities?.map((premiumActivity) => ({ ...premiumActivity })),
      prices: this.calculateProductsTotalPricesPrices(premiumActivities || []),
    };
  }

  private prepareUnaccompaniedMinorServicesForBreakdown(unaccompaniedMinorServices: QuoteOutputProduct[]) {
    if (!unaccompaniedMinorServices?.length) {
      return undefined;
    }
    return this.calculateProductsTotalPricesPrices(unaccompaniedMinorServices || []);
  }

  private calculateProductsTotalPricesPrices(products: QuoteOutputProduct[]): ProductPrices {
    return products?.reduce(
      (totalPrice, transfer) => {
        const { endPrice, price } = totalPrice;
        const calculateEndPrices = this.calculateTotalPrices(transfer.endPrice, endPrice);
        const calculatedPrices = this.calculateTotalPrices(transfer.price, price);

        return {
          endPrice: calculateEndPrices,
          price: calculatedPrices,
          discount: transfer.discountTotalAsPercentage,
        };
      },
      { endPrice: [], price: [] } as ProductPrices,
    );
  }
  /**
   * For a given list of prices, calculate the total prices for each currency.
   */
  private calculateTotalPrices(prices: LimitedCurrencyAmount[], totalPrices: Amount[]) {
    const calculatedTotalPrices = [...totalPrices];
    prices.forEach((price) => {
      const totalPriceForCurrency = calculatedTotalPrices.find((totalPrice) => totalPrice.currency === price.currency);
      if (!totalPriceForCurrency) {
        calculatedTotalPrices.push({ ...price });
      } else {
        totalPriceForCurrency.value += price.value;
      }
    });

    return calculatedTotalPrices;
  }
}
